import React, { useState } from 'react';
import {
  Box,
  Button,
  Divider,
  HeadingText,
  Icon,
  Modal,
  StackLayout,
  VisuallyHidden,
} from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import { NavLink, useLocation } from '@leagueplatform/routing';
import type {
  HeaderNavLink,
  UserMenuSection,
  UserMenuItem,
} from '@leagueplatform/solutions-foundation';

// Styles that mirror solutions-foundation's defaultMobileNavLinkStyles
const navLinkCss = {
  '&&': {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    color: '$onSurfaceTextSubdued',
    typography: '$subtitleTwo' as const,
    borderRadius: '$medium',
    padding: '$half',
    textDecoration: 'none',
    '&[aria-current="page"], &.active': {
      color: '$interactiveActionPrimary',
    },
    '&:hover': {
      backgroundColor: 'transparent',
      color: '$interactiveActionHovered',
    },
    '&[data-variant="critical"]': { color: '$onSurfaceTextCritical' },
    '&[data-variant="critical"]:hover, &[data-variant="critical"]:focus-visible':
      {
        color: '$onSurfaceTextCritical',
        backgroundColor: '$criticalBackgroundSubdued',
      },
  },
};

// Mirrors solutions-foundation's MobileNavLink — handles link/button variants
function MobileUserMenuItem({
  linkData,
  onLinkClick,
}: {
  linkData: UserMenuItem;
  onLinkClick: () => void;
}) {
  if ('to' in linkData) {
    return (
      <Box as="li" css={{ width: '100%' }}>
        <Box
          as={NavLink}
          to={linkData.to}
          id={`mobile-nav-link-${linkData.label.replace(/\s+/g, '-')}`}
          data-variant={linkData.variant}
          css={navLinkCss}
          onClick={onLinkClick}
        >
          {linkData.label}
        </Box>
      </Box>
    );
  }
  if ('onClick' in linkData) {
    return (
      <Box
        as="li"
        data-variant={linkData.variant}
        css={{ width: '100%' }}
        onClick={() => {
          onLinkClick();
          linkData.onClick();
        }}
      >
        <Button
          quiet
          size="medium"
          priority="tertiary"
          data-variant={linkData.variant}
          css={navLinkCss}
        >
          {linkData.label}
        </Button>
      </Box>
    );
  }
  return null;
}

// The drawer body — mirrors Ze (MobileNavDrawer) from solutions-foundation
// but sets tint="$onSurfaceIconPrimary" on nav link icons (unselected state).
function MobileNavDrawerContent({
  closeModal,
  navLinks,
  userMenu,
}: {
  closeModal: () => void;
  navLinks: HeaderNavLink[];
  userMenu: UserMenuSection[];
}) {
  const { formatMessage } = useIntl();
  const location = useLocation();

  return (
    <StackLayout as="nav" spacing="$one" horizontalAlignment="stretch">
      <Modal.Title asChild>
        <HeadingText level="2" size="xl" css={{ paddingLeft: '$half' }}>
          {formatMessage({ id: 'STR_MENU' })}
        </HeadingText>
      </Modal.Title>

      {/* Primary nav links */}
      <StackLayout as="ul" spacing="$half" horizontalAlignment="stretch">
        {navLinks.map(({ label, icon, to }) => {
          const isActive = location.pathname.startsWith(to as string);
          return (
            <Box as="li" key={label} css={{ width: '100%' }}>
              <Box
                as={NavLink}
                to={to}
                id={`${label.replace(/\s+/g, '-')}-link`}
                css={navLinkCss}
                onClick={closeModal}
              >
                {icon && (
                  <Icon
                    icon={isActive ? (`${icon}Filled` as typeof icon) : icon}
                    tint={
                      isActive
                        ? '$interactiveActionPrimary'
                        : '$onSurfaceIconPrimary'
                    }
                    css={{ marginRight: '$half' }}
                  />
                )}
                {label}
              </Box>
            </Box>
          );
        })}
      </StackLayout>

      {/* User menu sections */}
      {userMenu.map(({ heading, items }, idx) => (
        // eslint-disable-next-line react/no-array-index-key
        <StackLayout
          key={heading ?? idx}
          spacing="$oneAndHalf"
          css={{ width: '100%' }}
          horizontalAlignment="stretch"
        >
          <Divider css={{ margin: '0 $half', width: 'calc(100% - $half)' }} />
          <StackLayout spacing="$half" horizontalAlignment="stretch">
            {heading && (
              <HeadingText
                level="3"
                size="md"
                css={{
                  paddingLeft: '$half',
                  paddingTop: '$half',
                  paddingBottom: '$half',
                }}
              >
                {heading}
              </HeadingText>
            )}
            <StackLayout as="ul" spacing="$half" horizontalAlignment="stretch">
              {items.map((item) => (
                <MobileUserMenuItem
                  key={item.label}
                  linkData={item}
                  onLinkClick={closeModal}
                />
              ))}
            </StackLayout>
          </StackLayout>
        </StackLayout>
      ))}
    </StackLayout>
  );
}

// Drop-in replacement for solutions-foundation's MobileNavBar.
// Renders the logo + hamburger trigger + modal drawer with icon-primary tinted icons.
export function CustomMobileNav({
  navLinks,
  userMenu,
  logo,
}: {
  navLinks: HeaderNavLink[];
  userMenu: UserMenuSection[];
  logo?: React.ReactElement;
}) {
  const [open, setOpen] = useState(false);
  const { formatMessage } = useIntl();

  return (
    <>
      <Box as="li" css={{ margin: '$half' }}>
        {logo}
      </Box>
      <Modal.Root open={open} onOpenChange={setOpen}>
        <Box as="li" css={{ marginLeft: 'auto' }}>
          <Modal.Trigger asChild>
            <Button
              icon="interfaceMenu"
              hideLabel
              quiet
              priority="tertiary"
              size="large"
              css={{ margin: '$half $quarter', color: '$onSurfaceIconPrimary' }}
              aria-controls="user_dropdown_menu"
            >
              <VisuallyHidden>
                {formatMessage({ id: 'STR_OPEN_MENU' })}
              </VisuallyHidden>
            </Button>
          </Modal.Trigger>
        </Box>
        <Modal.Content
          id="user_dropdown_menu"
          layout="right"
          css={{
            '.GDS-modal': { maxWidth: 335 },
            '.GDS-modal-content': { paddingLeft: '$one' },
            '.GDS-modal-close-button': { color: '$interactiveActionSubdued' },
          }}
        >
          <MobileNavDrawerContent
            closeModal={() => setOpen(false)}
            navLinks={navLinks}
            userMenu={userMenu}
          />
        </Modal.Content>
      </Modal.Root>
    </>
  );
}
